import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Link, Navigate, Outlet, ScrollRestoration, useLocation } from 'react-router';
import { BASE, IS_DEMO_PATH } from './lib/base';
import { can, HOME_PATH, type Permission } from '@pace/domain';
import { Spinner } from '@pace/ui';
import { useRejectionToasts } from './components/actions';
import { useI18n } from './i18n';
import { useLiveStream } from './live/stream';
import { consumeLoggedOut, useSession } from './session';
import { useSetupStatus } from './data/setup';
import LoginPage from './routes/LoginPage';
import { NotFound, RouteError } from './routes/NotFound';
import WorkPage from './routes/WorkPage';

// The stage owner's work list loads with the first bundle (phones, most users); the rest on demand.
const ProjectsPage = lazy(() => import('./routes/ProjectsPage'));
const ProjectPage = lazy(() => import('./routes/project/ProjectPage'));
const DashboardPage = lazy(() => import('./routes/DashboardPage'));
const DisplayPage = lazy(() => import('./routes/DisplayPage'));
const ReportsPage = lazy(() => import('./routes/ReportsPage'));
const AdminPage = lazy(() => import('./routes/admin/AdminPage'));
const LandingPage = lazy(() => import('./routes/LandingPage'));
const WelcomePage = lazy(() => import('./routes/WelcomePage'));
const MePage = lazy(() => import('./routes/MePage'));
const SetupPage = lazy(() => import('./routes/SetupPage'));
const PairPage = lazy(() => import('./routes/SetupPage').then((m) => ({ default: m.PairPage })));

function Loading() {
  const { t } = useI18n();
  return <Spinner label={t('common.loading')} />;
}

/** Signed-in area: live stream + global rejection toasts; redirects to login otherwise. */
function Authed() {
  const { session, loading } = useSession();
  const loc = useLocation();
  useLiveStream(!!session);
  useRejectionToasts();
  if (loading) return <Loading />;
  if (!session) {
    // Remember where they were going (bookmark / expired session) and come back after the PIN.
    const next = consumeLoggedOut() ? '' : loc.pathname + loc.search;
    return (
      <Navigate to={next && next !== '/' ? `/login?next=${encodeURIComponent(next)}` : '/login'} replace />
    );
  }
  return (
    <Suspense fallback={<Loading />}>
      <Outlet />
    </Suspense>
  );
}

function Guard({ permission, children }: { permission: Permission; children: ReactNode }) {
  const { session } = useSession();
  const { t } = useI18n();
  if (!session) return null;
  if (!session.user.roles.some((r) => can(r, permission))) {
    return (
      <div className="center">
        <p>{t('common.notAllowed')}</p>
        <Link className="btn btn--primary" to={HOME_PATH[session.role]}>
          {t('common.goHome')}
        </Link>
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * A fresh server has no plant yet: every page except the public landing page goes to
 * the first-run wizard until the plant and first Admin exist (product mode). Demo
 * servers are seeded and skip this.
 */
function SetupGate() {
  const status = useSetupStatus();
  const loc = useLocation();
  if (status.isPending) return <Loading />;
  const fresh = status.data && !status.data.initialised;
  // The public landing page stays visible; everything else leads into setup.
  const open = loc.pathname === '/welcome' || (loc.pathname === '/' && !IS_DEMO_PATH);
  if (fresh && !open) return <Navigate to="/welcome" replace />;
  return (
    <Suspense fallback={<Loading />}>
      {/* New pages open at the top; Back returns to where you were on the page. */}
      <ScrollRestoration />
      <Outlet />
    </Suspense>
  );
}

/**
 * "/": signed-in people go to their own screen; signed-out visitors see the landing
 * page (the demo at /demo goes straight to its login instead).
 */
function Home() {
  const { session, loading } = useSession();
  if (loading) return <Loading />;
  if (session) return <Navigate to={HOME_PATH[session.role]} replace />;
  if (IS_DEMO_PATH) return <Navigate to="/login" replace />;
  return (
    <Suspense fallback={<Loading />}>
      <LandingPage />
    </Suspense>
  );
}

export const routes = [
  {
    errorElement: <RouteError />,
    element: <SetupGate />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/welcome', element: <WelcomePage /> },
      { path: '/login', element: <LoginPage /> },
      { path: '/login/:role', element: <LoginPage /> },
      {
        path: '/setup',
        element: (
          <Suspense fallback={<Loading />}>
            <SetupPage />
          </Suspense>
        ),
      },
      {
        path: '/pair',
        element: (
          <Suspense fallback={<Loading />}>
            <PairPage />
          </Suspense>
        ),
      },
      {
        element: <Authed />,
        children: [
          {
            path: '/work/:tab?',
            element: (
              <Guard permission="step.update">
                <WorkPage />
              </Guard>
            ),
          },
          { path: '/projects', element: <ProjectsPage /> },
          { path: '/projects/:id', element: <ProjectPage /> },
          { path: '/me', element: <MePage /> },
          {
            path: '/dashboard',
            element: (
              <Guard permission="dashboard.view">
                <DashboardPage />
              </Guard>
            ),
          },
          {
            path: '/reports',
            element: (
              <Guard permission="reports.view">
                <ReportsPage />
              </Guard>
            ),
          },
          {
            path: '/display',
            element: (
              <Guard permission="display.view">
                <DisplayPage />
              </Guard>
            ),
          },
          {
            path: '/admin/:section?',
            element: (
              <Guard permission="admin.manage">
                <AdminPage />
              </Guard>
            ),
          },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
];

export const createRouter = () => createBrowserRouter(routes, BASE ? { basename: BASE } : {});
